<script setup lang="ts">
type TimeData = {
    current: number;
    previous: number;
    show: boolean;
    label?: string;
};
const {
    showDays,
    hideHours,
    hideMinutes,
    hideSeconds,
    deadline,
    size = 'big',
    loading,
    color = 'blue',
    noDelay = false,
} = defineProps<IProps>();

const emit = defineEmits<{
    timeout: [];
}>();

function twoDigits(value: string | number) {
    if (Number(value) <= 0) {
        return '00';
    }
    if (value.toString().length <= 1) {
        return `0${value.toString()}`;
    }
    return value.toString();
}

interface IProps {
    deadline: Date;
    showDays?: boolean;
    hideHours?: boolean;
    hideMinutes?: boolean;
    hideSeconds?: boolean;
    size?: 'big' | 'small';
    color?: 'blue' | 'yellow' | 'green';
    loading?: boolean;
    noDelay?: boolean;
}

const dayData = ref<TimeData>({
    current: 0,
    previous: 0,
    show: showDays,
});
const hourData = ref<TimeData>({
    current: 0,
    previous: 0,
    show: !hideHours,
});
const minuteData = ref<TimeData>({
    current: 0,
    previous: 0,
    show: !hideMinutes,
});
const secondData = ref<TimeData>({
    current: 0,
    previous: 0,
    show: !hideSeconds,
});

const interval = ref(0);
const dayFlip = ref(false);
const hourFlip = ref(false);
const minuteFlip = ref(false);
const secondFlip = ref(false);
const timeData = computed<TimeData[]>(() => [
    {
        ...secondData.value,
        label: 'ثانیه',
    },
    {
        ...minuteData.value,
        label: 'دقیقه',
    },
    {
        ...hourData.value,
        label: 'ساعت',
    },
    {
        ...dayData.value,
        label: 'روز',
    },
]);
const flipData = computed(() => [
    secondFlip.value,
    minuteFlip.value,
    hourFlip.value,
    dayFlip.value,
]);
const updateAllData = () => {
    updateDay();
    updateHour();
    updateMinute();
    updateSecond();
};
const updateDay = () => {
    const now = new Date();
    const diff = Math.trunc(dayConfig.gregory(deadline).diff(now, 'day'));
    if (dayData.value.current !== diff) {
        dayData.value.previous = dayData.value.current;
        dayData.value.current = diff;
        dayFlip.value = true;
        setTimeout(() => {
            dayFlip.value = false;
        }, 500);
    }
};
const updateHour = () => {
    const now = new Date();
    const diff = Math.trunc(dayConfig.gregory(deadline).diff(now, 'hour') % 24);
    if (hourData.value.current !== diff) {
        hourData.value.previous = hourData.value.current;
        hourData.value.current = diff;
        hourFlip.value = true;
        setTimeout(() => {
            hourFlip.value = false;
        }, 500);
    }
};
const updateMinute = () => {
    const now = new Date();
    const diff = Math.trunc(
        dayConfig.gregory(deadline).diff(now, 'minute') % 60
    );
    if (minuteData.value.current !== diff) {
        minuteData.value.previous = minuteData.value.current;
        minuteData.value.current = diff;
        minuteFlip.value = true;
        setTimeout(() => {
            minuteFlip.value = false;
        }, 500);
    }
};
const updateSecond = () => {
    const now = new Date();
    const secondDiff = dayConfig.gregory(deadline).diff(now, 'second');
    const diff = Math.trunc(secondDiff % 60);
    if (secondDiff <= 0) {
        emit('timeout');
        window.clearInterval(interval.value);
    }
    if (secondData.value.current !== diff) {
        secondData.value.previous = secondData.value.current;
        secondData.value.current = diff;
        secondFlip.value = true;
        setTimeout(() => {
            secondFlip.value = false;
        }, 500);
    }
};
onMounted(() => {
    interval.value = window.setInterval(updateAllData, noDelay ? 0 : 1000);
});
onBeforeUnmount(() => {
    if (interval.value) {
        window.clearInterval(interval.value);
    }
});
</script>

<template>
    <div
        class="count-down-timer-container"
        :class="[size, color]"
        :style="{
            '--text':
                color === 'yellow' || color === 'green' ? 'white' : 'unset',
        }"
    >
        <template v-for="(data, index) in timeData" :key="index">
            <span
                v-show="data.show"
                class="flip-clock__piece"
                :class="{
                    flip: flipData[index],
                }"
            >
                <span class="flip-card">
                    <b
                        class="flip-card__top"
                        :class="{ 'loading-skeleton': loading }"
                    >
                        {{ twoDigits(data.current) }}
                    </b>
                    <b
                        class="flip-card__bottom"
                        :class="{ 'loading-skeleton': loading }"
                        :data-value="twoDigits(data.current)"
                    />
                    <b
                        class="flip-card__back"
                        :data-value="twoDigits(data.previous)"
                    />
                    <b
                        class="flip-card__back-bottom"
                        :data-value="twoDigits(data.previous)"
                    />
                </span>
                <span v-if="!loading && data.label" class="flip-clock__slot">
                    {{ data.label }}
                </span>
            </span>
        </template>
    </div>
</template>

<style scoped lang="scss">
.count-down-timer-container {
    display: flex;
    text-align: center;
    perspective: 600px;
    margin: 0 auto;
    *,
    *:before,
    *:after {
        box-sizing: border-box;
    }
    --size: 80px;
    @include mobile-only {
        --size: 60px;
    }
    --rounded: 15px;
    --light: #{usePalette()};
    --dark: #{darken(usePalette(), 5%)};
    &.small {
        --size: 48px;
        @include mobile-only {
            --size: 48px;
        }
        --rounded: 8px;
        .flip-clock__piece {
            margin-inline: 4px;
            .flip-card {
                @include tg-display4($w7);
            }
            .flip-clock__slot {
                @include tg-m($w5, usePalette($p-cool-gray, $p-700));
                text-align: center;
                @include mobile-only {
                    @include tg-m($w5, usePalette($p-gray, $p-700));
                    text-align: center;
                }
            }
        }
    }
    &.yellow {
        --light: #{usePalette($p-yellow)};
        --dark: #{darken(usePalette($p-yellow), 5%)};
    }
    &.green {
        --light: #{usePalette($p-green)};
        --dark: #{darken(usePalette($p-green), 5%)};
    }
    .flip-clock__piece {
        display: flex;
        flex-direction: column;
        justify-content: center;
        margin-inline: 8px;
        .flip-card {
            display: block;
            position: relative;
            padding-bottom: calc(var(--size) / 2);
            @include tg-display1($w7);
            @include mobile-only {
                @include tg-display2($w7);
            }
            .flip-card__top {
                height: calc(var(--size) / 2);
                color: var(--text, usePalette($p-gray, $p-500));
                background: var(--dark);
                padding-top: calc(var(--size) / 8);
                display: flex;
                justify-content: center;
                border-radius: var(--rounded) var(--rounded) 0 0;
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
                transform-style: preserve-3d;
                width: var(--size);
            }
            .flip-card__bottom {
                height: calc(var(--size) / 2);
                background: var(--dark);
                padding-top: calc(var(--size) / 8);
                display: flex;
                justify-content: center;
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
                transform-style: preserve-3d;
                width: var(--size);
                color: var(--text, usePalette($p-gray, $p-300));
                position: absolute;
                top: 50%;
                left: 0;
                border-top: solid 1px var(--dark);
                background: var(--light);
                border-radius: 0 0 var(--rounded) var(--rounded);
                pointer-events: none;
                overflow: hidden;
                z-index: 2;
                &::after {
                    display: block;
                    margin-top: calc(-1 * (var(--size) / 2));
                    content: attr(data-value);
                }
            }

            .flip-card__back-bottom {
                height: calc(var(--size) / 2);
                background: var(--dark);
                padding-top: calc(var(--size) / 8);
                display: flex;
                justify-content: center;
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
                transform-style: preserve-3d;
                width: var(--size);
                color: var(--text, usePalette($p-gray, $p-300));
                position: absolute;
                top: 50%;
                left: 0;
                border-top: solid 1px var(--dark);
                background: var(--light);
                border-radius: 0 0 var(--rounded) var(--rounded);
                pointer-events: none;
                overflow: hidden;
                z-index: 1;
                &::after {
                    display: block;
                    margin-top: calc(-1 * (var(--size) / 2));
                    content: attr(data-value);
                }
            }

            .flip-card__back {
                &::after {
                    height: calc(var(--size) / 2);
                    color: var(--text, usePalette($p-gray, $p-500));
                    background: var(--dark);
                    padding-top: calc(var(--size) / 8);
                    display: flex;
                    justify-content: center;
                    border-radius: var(--rounded) var(--rounded) 0 0;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    transform-style: preserve-3d;
                    width: var(--size);
                }
                &::before {
                    display: block;
                    height: calc(var(--size) / 2);
                    color: var(--text, usePalette($p-gray, $p-500));
                    background: var(--dark);
                    border-radius: var(--rounded) var(--rounded) 0 0;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    transform-style: preserve-3d;
                    width: var(--size);
                    content: attr(data-value);
                    position: relative;
                    overflow: hidden;
                    z-index: -1;
                    padding: 0;
                }
                position: absolute;
                top: 0;
                height: 100%;
                left: 0;
                pointer-events: none;
            }
        }
        &.flip {
            .flip-card__bottom {
                transform-origin: center top;
                animation: flipBottom 0.6s cubic-bezier(0.15, 0.45, 0.28, 1)
                    both;
            }
            .flip-card__back::before {
                z-index: 1;
                animation: flipTop 0.3s cubic-bezier(0.37, 0.01, 0.94, 0.35);
                animation-fill-mode: both;
                transform-origin: center bottom;
                padding-top: calc(var(--size) / 8);
                display: flex;
                justify-content: center;
            }
        }

        .flip-clock__slot {
            @include tg-xl($w5, usePalette($p-cool-gray, $p-700));
            text-align: center;
            @include mobile-only {
                @include tg-l($w5, usePalette($p-cool-gray, $p-700));
                text-align: center;
            }
            display: block;
        }
    }
}
.loading-skeleton {
    border-radius: inherit !important;
}
.flip-card__bottom {
    &.loading-skeleton {
        border-top: none !important;
    }
}
@keyframes flipTop {
    0% {
        transform: rotateX(0deg);
        z-index: 2;
    }
    0%,
    99% {
        opacity: 1;
    }
    100% {
        transform: rotateX(-90deg);
        opacity: 0;
    }
}
@keyframes flipBottom {
    0%,
    50% {
        z-index: -1;
        transform: rotateX(90deg);
        opacity: 0;
    }
    51% {
        opacity: 1;
    }
    100% {
        opacity: 1;
        transform: rotateX(0deg);
        z-index: 5;
    }
}
</style>
